<template>
  <div>
    <transition name="wrapper" :duration="500">
      <div v-show="visible" class="wrapper" @click.stop="handleHide">
        <transition name="bounce">
          <!-- 兼容动画 -->
          <div v-show="visible" class="modal px-4 md:px-10 md:pb-10 pt-0" @click.stop>
            <div class="bg-white pt-2 z-20 flex items-center justify-between">
              <div class="h-16 w-11/12 flex items-center">
                <div class="h-14">
                  <img src="./assets/detail-logo.svg" class="h-full" />
                </div>
                <div class="title max-w-7/10">
                  <span class="mx-3 text-lg max-w-full truncate">{{ content.title }}</span>
                </div>
              </div>
              <div class="flex items-center mx-2 md:mx-0">
                <div
                  class="hidden md:flex cursor-pointer items-center justify-center mx-4 screenshot"
                  @click.stop="handleScreenshot"
                >
                  <img src="./assets/Vector.svg" alt="截图" class="h-2/5 mr-2 select-none" />
                  <span>生成分享图</span>
                </div>
                <div class="w-4 h-4 cursor-pointer" @click.stop="handleHide">
                  <img src="./assets/close.svg" class="w-full h-full" alt="关闭" />
                </div>
              </div>
            </div>
            <div ref="reportView" class="flex-1 overflow-y-auto">
              <!-- 用于截图定位 class -->
              <div class="screenshotUserInfo"></div>
              <div v-if="multipleValue.size > 2" class="w-full sm:w-2/3 md:w-128 mx-auto">
                <!-- class chart 用于截图定位 -->
                <!-- 修改注意检查截图效果 -->
                <canvas ref="chart" class="chart w-full h-full my-12"></canvas>
              </div>
              <div :class="[multipleValue.size > 2 ? '' : 'mt-8']">
                <div v-for="(item, index) in reportContents" :key="index">
                  <div class="relative mb-2 flex items-center">
                    <div
                      class="flex items-center h-8 px-2 rounded-md text-white max-w-7/10"
                      :style="{
                        'background-color': reportColors[index % reportColors.length].label
                      }"
                    >
                      <span class="max-w-full truncate">{{ item.type }}</span>
                    </div>
                    <LabelIcon
                      :color="reportColors[index % reportColors.length].label"
                      class="z-0 h-8 w-6 -ml-1"
                    />
                  </div>
                  <div
                    class="rounded-md mb-12 p-6 font-extralight"
                    :style="{
                      'background-color': reportColors[index % reportColors.length].content
                    }"
                  >
                    <div class="prose max-w-full content" v-html="item.content"></div>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="md:hidden flex items-center justify-center w-full py-4 z-20 bg-white flex-1"
            >
              <div
                class="flex cursor-pointer items-center justify-center screenshot"
                @click.stop="handleScreenshot"
              >
                <img src="./assets/Vector.svg" alt="截图" class="h-2/5 mr-2 select-none" />
                <span>生成分享图</span>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
    <PreviewScreemshot
      :url.sync="screenshotUrl"
      :visible.sync="screenshotModelVisible"
      :title="content.title || courseName"
    />
  </div>
</template>
<script lang="ts">
import { Content } from '@forchange/storytime';
import { Vue, Component, Prop } from 'vue-property-decorator';
import LabelIcon from './assets/LabelIcon.vue';

import * as echarts from 'echarts/core';
import { TitleComponent, LegendComponent } from 'echarts/components';
import { RadarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import PreviewScreemshot from './components/PreviewScreemshot.vue';
import ScreenshotUserInfo from './components/UserInfo.vue';

echarts.use([TitleComponent, LegendComponent, RadarChart, CanvasRenderer]);

import { namespace } from 'vuex-class';
import { Score, UserInfo } from '@/store/modules/common/types';
import showdown from 'showdown';

import Html2canvas from 'html2canvas';

export type AnalysisReports = Array<AnalysisReport>;
export type AnalysisReport = {
  type: string;
  analysis: Array<Report>;
};
export type Report = {
  start: number;
  end: number;
  report: string;
};

export type reportContents = Array<{ type: string; content: string; scoreValue: number }>;

const userModule = namespace('common');

@Component({
  components: {
    LabelIcon,
    PreviewScreemshot,
    ScreenshotUserInfo
  }
})
export default class Detail extends Vue {
  @Prop({ type: Object }) content!: Content & {
    content: AnalysisReports;
    title: string;
    multipleValue: string | undefined;
    showTopScoreType: true | '是';
  };
  @userModule.State('score') score!: Score;
  @userModule.State('courseName') courseName: string;
  @userModule.State('userInfo') public userInfo: UserInfo;
  @userModule.State('authingId') public authingId: string;

  visible = false;
  error = false;
  reportContents: reportContents = [];
  reportColors = [
    {
      label: '#806AE8',
      content: '#EEEBFA'
    },
    {
      label: '#7E88F5',
      content: '#E1E9FF'
    },
    {
      label: '#EE734F',
      content: '#FDEEEA'
    },
    {
      label: '#4CA6A8',
      content: '#E9F4F5'
    }
  ];

  screenshotModelVisible = false;
  screenshotUrl = '';

  get multipleValue() {
    return (
      (this.content.multipleValue &&
        (new Map(JSON.parse(this.content.multipleValue)) as Map<string, number>)) ||
      this.score.multipleValue
    );
  }

  mounted() {
    this.initReport();
    this.$nextTick(() => this.initChart());
  }

  open() {
    this.visible = true;
  }

  initReport() {
    this.reportContents = [];
    const converter = new showdown.Converter({
      simpleLineBreaks: true,
      openLinksInNewWindow: true
    });
    this.multipleValue.forEach((scoreValue, scoreKey) => {
      const targetReports = this.content.content.find((analysisReport) => {
        return analysisReport.type === scoreKey;
      });
      if (!targetReports) return;
      const targetReport = targetReports.analysis.find((analysis) => {
        return scoreValue >= analysis.start && scoreValue <= analysis.end;
      });
      if (!targetReport) return;
      this.reportContents.push({
        type: scoreKey,
        scoreValue,
        content: converter.makeHtml(targetReport.report)
      });
    });

    [true, '是'].includes(this.content.showTopScoreType) && this.limitMaxScore();

    this.insertReportToContent(this.reportContents);
  }

  /**
   * 截选分数最高的类型
   */
  limitMaxScore() {
    this.reportContents = this.reportContents
      .sort((firstItem, secedeItem) => secedeItem.scoreValue - firstItem.scoreValue)
      .reduce((previousValue, currentValue) => {
        if (previousValue.length && previousValue[0].scoreValue !== currentValue.scoreValue) {
          return previousValue;
        }
        previousValue.push(currentValue);
        return previousValue;
      }, [] as any);
  }

  initChart() {
    const labels: Array<string> = [];
    const scores: Array<number> = [];
    if (!this.multipleValue?.size) {
      this.error = true;
      return;
    }
    if (this.multipleValue?.size < 2) {
      return;
    }
    this.multipleValue.forEach((scoreValue, scoreKey) => {
      labels.push(scoreKey);
      scores.push(scoreValue);
    });
    const ctx = this.$refs.chart as HTMLCanvasElement;
    const option = this.getChartOption(labels, scores);
    const myChart = echarts.init(ctx, undefined, {
      devicePixelRatio: 2.5,
      width: 500,
      height: 400
    });
    option && myChart.setOption(option);
  }

  getChartOption(labels: Array<string>, scores: Array<number>) {
    const maxScore = Math.floor(
      ([...scores].sort((beforeValue, afterValue) => afterValue - beforeValue)[0] || 1) * 1.1
    );

    const indicator = labels.map((item) => {
      return { text: item, max: maxScore };
    });

    const option = {
      radar: [
        {
          indicator,
          name: {
            formatter: function (text: string) {
              if (indicator[0].text === text) return text;
              let strlength = text.length;
              let copyText = text;
              if (strlength % 4 !== 0) {
                copyText = copyText.replace(/\S{4}/g, function (match) {
                  return match + '\n';
                });
              } else {
                copyText = copyText.replace(/\S{4}/g, function (match) {
                  return match + '\n';
                });
                strlength = copyText.length;
                copyText = copyText.substring(0, strlength - 1);
              }
              return copyText;
            }
          }
        }
      ],
      series: [
        {
          type: 'radar',
          data: [
            {
              value: scores,
              name: 'scores',
              symbol: 'circle',
              symbolSize: 8,
              symbolOffset: [2, 2],
              label: {
                show: true,
                formatter: function (params: { value: string }) {
                  return params.value;
                }
              },
              areaStyle: {
                color: 'rgba(245, 247, 255, 1)'
              },
              lineStyle: {
                color: '#433eff'
              },
              itemStyle: {
                color: '#433eff',
                borderWidth: 1,
                borderColor: '#fff'
              }
            }
          ]
        }
      ]
    };
    return option;
  }

  handleHide() {
    this.visible = false;
  }

  insertReportToContent(report: reportContents) {
    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        report
      }
    });
  }

  handleScreenshot() {
    this.screenshotModelVisible = true;
    setTimeout(async () => {
      try {
        const canvasSourceEl = this.cloneElement();
        if (!canvasSourceEl.cloneEl) {
          this.screenshotModelVisible = false;
          return;
        }
        await this.buildElement(canvasSourceEl.cloneEl);
        const options = {
          width: canvasSourceEl.cloneEl.offsetWidth,
          useCORS: true
        };
        const canvasEl = await Html2canvas(canvasSourceEl.cloneEl, options);
        this.screenshotUrl = canvasEl.toDataURL('image/png');
        canvasSourceEl.removeHook();
      } catch (error) {
        this.screenshotModelVisible = false;
        this.$Message.error({
          time: 1500,
          content: '分享图生成失败'
        });
        throw new Error('图片生成失败' + error);
      }
    }, 100);
  }

  cloneElement() {
    const el = (this.$refs.reportView as HTMLDivElement).cloneNode(true);
    const container = document.createElement('div');
    container.appendChild(el);
    container.setAttribute('id', 'canvas-view');
    document.body.appendChild(container);
    const cloneEl = document.body.querySelector('#canvas-view') as HTMLDivElement;
    return {
      cloneEl,
      removeHook: () => {
        document.body.removeChild(container);
      }
    };
  }

  async buildElement(el: HTMLDivElement) {
    el.setAttribute('class', 'max-w-960 p-8');
    const chartCanvas = this.$refs.chart as HTMLCanvasElement | undefined;
    if (chartCanvas) {
      const chartUrl = chartCanvas.toDataURL('image/png');
      const imgEl = document.createElement('img');
      imgEl.src = chartUrl;
      const chartEl = el.querySelector('.chart') as HTMLCanvasElement | undefined;
      chartEl?.parentElement?.replaceChild(imgEl, chartEl);
    }

    let url = '';
    if (this.userInfo.avator) {
      // 图片本地化
      try {
        url = await new Promise((resolve, reject) => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const img = document.createElement('img');
          img.setAttribute('crossorigin', 'anonymous');

          img.src = this.userInfo.avator;
          img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            ctx?.drawImage(img, 0, 0);
            resolve(canvas.toDataURL());
          };
          img.onerror = (e) => {
            reject(e);
          };
        });
      } catch (error) {
        console.log('error: ', error);
      }
    }

    // 添加头部用户信息组件
    const replaceEl = el.querySelector('.screenshotUserInfo');
    if (!replaceEl) return;
    const screenshotUserInfo = new ScreenshotUserInfo({
      propsData: {
        title: this.content.title || this.courseName,
        avator: url,
        nickname: this.getNick()
      }
    }).$mount();
    replaceEl.parentElement?.replaceChild(screenshotUserInfo.$el, replaceEl);
  }

  getNick() {
    return (
      this.userInfo.nickname ||
      `学员${
        (this.userInfo.phone && this.userInfo.phone.substring(this.userInfo.phone.length - 4)) ||
        (this.authingId && this.authingId.substring(this.authingId.length - 4))
      }`
    );
  }
}
</script>
<style scoped>
.wrapper {
  @apply fixed top-0 left-0 right-0 bottom-0 w-screen h-screen md:p-10 pt-20 z-20;
  background: rgba(0, 0, 0, 0.4);
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
  transition: all 1s;
}

.modal {
  @apply fixed md:relative bottom-0 md:top-1/2 w-full md:max-h-full max-w-960 mx-auto bg-white md:rounded-md overflow-y-auto flex flex-col;
  max-height: 90%;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}
@screen md {
  .modal {
    transform: translateY(-50%);
    animation: 0s;
  }
}

.title {
  @apply flex items-center relative;
}

.label {
  @apply absolute top-1/2 text-white w-24 text-center truncate;
  transform: translateY(-50%);
}

.content a {
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.wrapper-enter-active {
  transition: all 0.5;
}
.wrapper-leave-active {
  transition: all 0.5;
}
.wrapper-enter {
  background: rgba(0, 0, 0, 0);
}

.wrapper-leave-to {
  background: rgba(0, 0, 0, 0);
}

.bounce-enter-active {
  animation: fadeInUpBig_mb 0.5s;
}

.bounce-leave-active {
  animation: fadeInUpBig_mb 0.5s reverse;
}

@screen md {
  .bounce-enter-active {
    animation: fadeInUpBig 0.5s;
  }

  .bounce-leave-active {
    animation: fadeInUpBig 0.5s reverse;
  }
}

@keyframes fadeInUpBig {
  from {
    -webkit-transform: translate3d(0, 2000px, 0) translateY(-50%);
    transform: translate3d(0, 2000px, 0) translateY(-50%);
    opacity: 0;
  }

  to {
    -webkit-transform: translate3d(0, 0, 0) translateY(-50%);
    transform: translate3d(0, 0, 0) translateY(-50%);
    opacity: 1;
  }
}

@keyframes fadeInUpBig_mb {
  from {
    -webkit-transform: translate3d(0, 2000px, 0);
    transform: translate3d(0, 2000px, 0);
    opacity: 0;
  }

  to {
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
    opacity: 1;
  }
}

.screenshot {
  @apply text-sm md:text-xs w-36 h-10 rounded-md text-motif-500 bg-undertint hover:opacity-hover;
}
</style>
